import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Autocomplete,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Card,
  CardContent,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Person,
  DirectionsCar,
  LocationOn,
  CalendarToday,
  Payment,
  Note,
  Phone,
  Email,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vehicleAPI } from '../../services/vehicleAPI';
import driverService from '../../services/driverService';
import { adminApiService } from '../../services/adminApiService';

export interface CreateBookingData {
  type: 'self-drive' | 'chauffeur-driven' | 'driver-rental';
  customer: {
    id?: string; // Customer's user ID - REQUIRED for backend
    name: string;
    email: string;
    phone: string;
    address?: string;
  };
  vehicle: {
    id?: string; // Vehicle ID - REQUIRED for rentals
    make: string;
    model: string;
    year?: number;
    category?: string;
  };
  driver?: {
    id?: string; // Driver ID - REQUIRED for driver rentals
    name?: string;
    rating?: number;
  };
  schedule: {
    startDate: Date | null;
    endDate: Date | null;
    startTime?: string;
    endTime?: string;
    pickupLocation: string;
    dropoffLocation?: string;
  };
  pricing: {
    dailyRate?: number;
    totalDays?: number;
    subtotal?: number;
    taxes?: number;
    fees?: number;
    total?: number;
  };
  paymentMethod?: string;
  paymentStatus?: string;
  status?: string;
  notes?: string;
  specialRequests?: string[];
}

interface CreateBookingDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBookingData) => Promise<void>;
  initialType?: 'self-drive' | 'chauffeur-driven' | 'driver-rental';
  availableVehicles?: any[];
  availableDrivers?: any[];
  loading?: boolean;
}

const bookingTypeOptions = [
  { value: 'self-drive', label: 'Flex Package', icon: '🚗', description: 'Self-drive vehicle rental (no driver)' },
  { value: 'chauffeur-driven', label: 'Premium Package', icon: '🎩', description: 'Car rental with professional driver (chauffeur service)' },
  { value: 'driver-rental', label: 'Driver Rental Only', icon: '👨‍✈️', description: 'Rent a professional driver for your vehicle' },
];

const specialRequestOptions = [
  'Airport pickup/dropoff',
  'GPS navigation system',
  'Child safety seats',
  'Additional driver',
  'Insurance coverage upgrade',
  'Fuel service',
  '24/7 roadside assistance',
  'Luxury amenities package',
];

const popularLocations = [
  'Lagos Island, Lagos',
  'Victoria Island, Lagos',
  'Ikoyi, Lagos',
  'Lekki, Lagos',
  'Ikeja, Lagos',
  'Murtala Muhammed Airport, Lagos',
  'Abuja City Centre',
  'Nnamdi Azikiwe Airport, Abuja',
  'Port Harcourt',
  'Kano',
];

export const CreateBookingDialog: React.FC<CreateBookingDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialType = 'self-drive',
  availableVehicles = [],
  availableDrivers = [],
  loading = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CreateBookingData>({
    type: initialType,
    customer: {
      id: undefined, // Will be set when customer is selected
      name: '',
      email: '',
      phone: '',
      address: '',
    },
    vehicle: {
      id: undefined, // Will be set when vehicle is selected
      make: '',
      model: '',
      category: '',
    },
    driver: {
      id: undefined, // Will be set when driver is selected
    },
    schedule: {
      startDate: null,
      endDate: null,
      startTime: '09:00',
      endTime: '17:00',
      pickupLocation: '',
      dropoffLocation: '',
    },
    pricing: {
      dailyRate: 0,
      totalDays: 1,
      subtotal: 0,
      taxes: 0,
      fees: 0,
      total: 0,
    },
    paymentMethod: 'Bank Transfer',
    paymentStatus: 'pending',
    status: 'pending',
    notes: '',
    specialRequests: [],
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fetchedVehicles, setFetchedVehicles] = useState<any[]>([]);
  const [fetchedDrivers, setFetchedDrivers] = useState<any[]>([]);
  const [fetchedUsers, setFetchedUsers] = useState<any[]>([]);
  const [fetchingOptions, setFetchingOptions] = useState(false);
  const [sameAsPickup, setSameAsPickup] = useState(false);
  const [hasPastDates, setHasPastDates] = useState(false);
  const [showPastDateConfirmation, setShowPastDateConfirmation] = useState(false);

  const steps = [
    'Booking Type',
    'Customer Information',
    'Vehicle & Driver',
    'Schedule & Location',
    'Pricing & Extras',
    'Review & Confirm'
  ];

  useEffect(() => {
    if (formData.schedule.startDate && formData.schedule.endDate) {
      const days = Math.ceil(
        (formData.schedule.endDate.getTime() - formData.schedule.startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const subtotal = (formData.pricing.dailyRate || 0) * days;
      const taxes = subtotal * 0.075; // 7.5% VAT
      const fees = formData.type === 'with-driver' ? 5000 : 1000;
      const total = subtotal + taxes + fees;

      setFormData(prev => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          totalDays: days,
          subtotal,
          taxes,
          fees,
          total,
        }
      }));
    }
  }, [formData.schedule.startDate, formData.schedule.endDate, formData.pricing.dailyRate, formData.type]);

  // Check if selected dates are in the past
  useEffect(() => {
    if (!formData.schedule.startDate) {
      setHasPastDates(false);
      return;
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for fair comparison

    const selectedDate = new Date(formData.schedule.startDate);
    selectedDate.setHours(0, 0, 0, 0); // Reset selected date to start of day too

    const isPastDate = selectedDate < now;

    setHasPastDates(isPastDate);
  }, [formData.schedule.startDate]);

  // Fetch available users, vehicles and drivers when needed
  useEffect(() => {
    const fetchOptions = async () => {
      if (!open) return;

      setFetchingOptions(true);
      try {
        // Fetch users (customers), vehicles, and drivers
        const [usersRes, vehiclesRes, driversRes] = await Promise.all([
          adminApiService.getUsers({ limit: 100 }).catch((err) => {
            console.error('❌ Failed to fetch users:', err);
            return { success: true, data: { users: [], statistics: {} } };
          }),
          vehicleAPI.getVehicles().catch((err) => {
            console.error('❌ Failed to fetch vehicles:', err);
            return { vehicles: [] };
          }),
          driverService.getDrivers({}, 1, 50).catch((err) => {
            console.error('❌ Failed to fetch drivers:', err);
            return { drivers: [] };
          })
        ]);

        const users = usersRes.data?.users || [];
        const vehicles = vehiclesRes.vehicles || [];
        const drivers = driversRes.drivers || [];


        setFetchedUsers(users);
        setFetchedVehicles(vehicles);
        setFetchedDrivers(drivers);

        // Show warning if no data was fetched
        if (users.length === 0) {
        }
        if (vehicles.length === 0) {
        }
      } catch (error) {
        console.error('❌ Critical error fetching booking options:', error);
      } finally {
        setFetchingOptions(false);
      }
    };

    fetchOptions();
  }, [open, formData.type]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // Booking Type
        if (!formData.type) newErrors.type = 'Please select a booking type';
        break;
      case 1: // Customer Information
        if (!formData.customer.id) newErrors.customerId = 'Please select a customer from the list';
        if (!formData.customer.name) newErrors.customerName = 'Customer name is required';
        if (!formData.customer.email) newErrors.customerEmail = 'Customer email is required';
        if (!formData.customer.phone) newErrors.customerPhone = 'Customer phone is required';
        if (formData.customer.email && !/\S+@\S+\.\S+/.test(formData.customer.email)) {
          newErrors.customerEmail = 'Please enter a valid email';
        }
        break;
      case 2: // Vehicle & Driver
        // Vehicle is required for rental and with-driver types
        if ((formData.type === 'rental' || formData.type === 'with-driver') && !formData.vehicle.id && !formData.vehicle.make) {
          newErrors.vehicle = 'Please select a vehicle';
        }
        // Driver is required for with-driver and driver-hire types
        if ((formData.type === 'with-driver' || formData.type === 'driver-hire') && !formData.driver?.id && !formData.driver?.name) {
          newErrors.driver = 'Driver selection is required for this booking type';
        }
        break;
      case 3: // Schedule & Location
        if (!formData.schedule.startDate) newErrors.startDate = 'Start date is required';
        if (!formData.schedule.endDate) newErrors.endDate = 'End date is required';
        if (!formData.schedule.pickupLocation) newErrors.pickupLocation = 'Pickup location is required';
        if (formData.schedule.startDate && formData.schedule.endDate && 
            formData.schedule.startDate >= formData.schedule.endDate) {
          newErrors.endDate = 'End date must be after start date';
        }
        break;
      case 4: // Pricing
        if (!formData.pricing.dailyRate || formData.pricing.dailyRate <= 0) {
          newErrors.dailyRate = 'Daily rate must be greater than 0';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {

    const isValid = validateStep(activeStep);

    if (isValid) {
      setActiveStep(prev => prev + 1);
    } else {
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (validateStep(activeStep)) {
      // If booking has past dates, show confirmation dialog
      if (hasPastDates) {
        setShowPastDateConfirmation(true);
      } else {
        await performSubmit();
      }
    }
  };

  const performSubmit = async () => {
    try {
      await onSubmit(formData);
      handleReset();
      setShowPastDateConfirmation(false);
      onClose();
    } catch (error) {
      console.error('Failed to create booking:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      type: initialType,
      customer: { name: '', email: '', phone: '', address: '' },
      vehicle: { make: '', model: '', category: '' },
      schedule: {
        startDate: null,
        endDate: null,
        startTime: '09:00',
        endTime: '17:00',
        pickupLocation: '',
        dropoffLocation: '',
      },
      pricing: { dailyRate: 0, totalDays: 1, subtotal: 0, taxes: 0, fees: 0, total: 0 },
      notes: '',
      specialRequests: [],
    });
    setActiveStep(0);
    setErrors({});
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Booking Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose the type of service you'd like to book
              </Typography>
            </Grid>
            {bookingTypeOptions.map((option) => (
              <Grid item xs={12} sm={4} key={option.value}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: formData.type === option.value ? 3 : 1,
                    borderColor: formData.type === option.value ? 'primary.main' : 'divider',
                    bgcolor: formData.type === option.value ? 'primary.50' : 'background.paper',
                    boxShadow: formData.type === option.value ? 3 : 1,
                    transform: formData.type === option.value ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => setFormData(prev => ({ ...prev, type: option.value as any }))}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3, position: 'relative' }}>
                    {formData.type === option.value && (
                      <Chip
                        label="Selected"
                        color="primary"
                        size="small"
                        sx={{ position: 'absolute', top: 10, right: 10 }}
                      />
                    )}
                    <Typography variant="h3" sx={{ mb: 1 }}>{option.icon}</Typography>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: formData.type === option.value ? 700 : 600,
                        color: formData.type === option.value ? 'primary.main' : 'text.primary'
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Please fix the following errors:
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {Object.entries(errors).map(([key, value]) => (
                      <li key={key}>{value}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Grid>

            {/* User Selection Autocomplete */}
            <Grid item xs={12}>
              {!fetchingOptions && fetchedUsers.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No users available. Please check your API connection or contact support.
                </Alert>
              )}
              <Autocomplete
                options={fetchedUsers}
                getOptionLabel={(option) =>
                  `${option.personalInfo?.firstName || ''} ${option.personalInfo?.lastName || ''} (${option.personalInfo?.email || option.email || ''})`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer *"
                    placeholder="Search for an existing customer..."
                    error={!!errors.customerId}
                    helperText={errors.customerId || 'Required: Select an existing customer from the database'}
                  />
                )}
                onChange={(e, selectedUser) => {
                  if (selectedUser) {
                    setFormData(prev => ({
                      ...prev,
                      customer: {
                        id: selectedUser.id,
                        name: `${selectedUser.personalInfo?.firstName || ''} ${selectedUser.personalInfo?.lastName || ''}`.trim(),
                        email: selectedUser.personalInfo?.email || selectedUser.email || '',
                        phone: selectedUser.personalInfo?.phone || selectedUser.phone || '',
                        address: selectedUser.personalInfo?.address || ''
                      }
                    }));
                    // Clear customer-related errors when a user is selected
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.customerId;
                      delete newErrors.customerName;
                      delete newErrors.customerEmail;
                      delete newErrors.customerPhone;
                      return newErrors;
                    });
                  }
                }}
                loading={fetchingOptions}
                noOptionsText={fetchingOptions ? "Loading users..." : "No users found"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={formData.customer.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customer: { ...prev.customer, name: e.target.value }
                }))}
                error={!!errors.customerName}
                helperText={errors.customerName}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                required
                value={formData.customer.email}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customer: { ...prev.customer, email: e.target.value }
                }))}
                error={!!errors.customerEmail}
                helperText={errors.customerEmail}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                required
                value={formData.customer.phone}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customer: { ...prev.customer, phone: e.target.value }
                }))}
                error={!!errors.customerPhone}
                helperText={errors.customerPhone}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address (Optional)"
                value={formData.customer.address}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customer: { ...prev.customer, address: e.target.value }
                }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {formData.type === 'driver-hire' ? 'Driver Selection' :
                 formData.type === 'rental' ? 'Vehicle Selection' :
                 'Vehicle & Driver Selection'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formData.type === 'driver-hire' ? 'Select a professional driver for your vehicle' :
                 formData.type === 'rental' ? 'Choose a vehicle for self-drive rental' :
                 'Select both vehicle and chauffeur for Premium Package'}
              </Typography>
            </Grid>

            {/* Vehicle Selection - Only show for rental and with-driver types */}
            {formData.type !== 'driver-hire' && (
              <>
                {fetchedVehicles.length > 0 ? (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Vehicle Make & Model</InputLabel>
                      <Select
                        value={formData.vehicle.id || ''}
                        label="Vehicle Make & Model"
                        onChange={(e) => {
                          const selectedVehicle = fetchedVehicles.find(v => v.id === e.target.value);
                          if (selectedVehicle) {
                            setFormData(prev => ({
                              ...prev,
                              vehicle: {
                                id: selectedVehicle.id,
                                make: selectedVehicle.make || selectedVehicle.vehicle_make,
                                model: selectedVehicle.model || selectedVehicle.vehicle_model,
                                year: selectedVehicle.year,
                                category: selectedVehicle.category || selectedVehicle.type,
                              },
                              pricing: {
                                ...prev.pricing,
                                dailyRate: selectedVehicle.dailyRate || selectedVehicle.pricePerDay || 0,
                              }
                            }));
                          }
                        }}
                        disabled={fetchingOptions}
                      >
                        {fetchedVehicles.map((vehicle: any) => (
                          <MenuItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.year} {vehicle.make || vehicle.vehicle_make} {vehicle.model || vehicle.vehicle_model} - ₦{(vehicle.dailyRate || vehicle.pricePerDay || 0).toLocaleString()}/day
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vehicle Make"
                        required
                        value={formData.vehicle.make}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          vehicle: { ...prev.vehicle, make: e.target.value }
                        }))}
                        error={!!errors.vehicle}
                        helperText={errors.vehicle}
                        placeholder="e.g., Toyota, Honda, BMW"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vehicle Model"
                        required
                        value={formData.vehicle.model}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          vehicle: { ...prev.vehicle, model: e.target.value }
                        }))}
                        placeholder="e.g., Camry, Accord, X5"
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Vehicle Category</InputLabel>
                    <Select
                      value={formData.vehicle.category || ''}
                      label="Vehicle Category"
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        vehicle: { ...prev.vehicle, category: e.target.value }
                      }))}
                    >
                      <MenuItem value="economy">Economy</MenuItem>
                      <MenuItem value="compact">Compact</MenuItem>
                      <MenuItem value="standard">Standard</MenuItem>
                      <MenuItem value="luxury">Luxury</MenuItem>
                      <MenuItem value="suv">SUV</MenuItem>
                      <MenuItem value="van">Van</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Suggestion for driver-hire to also book a car */}
            {formData.type === 'driver-hire' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Also need a car to book? Consider our <strong>Premium Package</strong> (car rental with professional driver) or <strong>Flex Package</strong> (self-drive vehicle rental).
                  </Typography>
                </Alert>
              </Grid>
            )}
            {/* Driver Selection - Show for driver-hire and with-driver types */}
            {(formData.type === 'driver-hire' || formData.type === 'with-driver') && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={formData.driver?.id || ''}
                    label="Driver"
                    onChange={(e) => {
                      const selectedDriver = fetchedDrivers.find(d => d.id === e.target.value);
                      if (selectedDriver) {
                        setFormData(prev => ({
                          ...prev,
                          driver: {
                            id: selectedDriver.id,
                            name: selectedDriver.personalInfo?.firstName + ' ' + selectedDriver.personalInfo?.lastName || selectedDriver.firstName + ' ' + selectedDriver.lastName,
                            rating: selectedDriver.rating,
                          }
                        }));
                      }
                    }}
                    disabled={fetchingOptions}
                    required
                  >
                    {fetchedDrivers.map((driver: any) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.personalInfo?.firstName || driver.firstName} {driver.personalInfo?.lastName || driver.lastName} - ⭐{driver.rating || 0}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        );

      case 3:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Schedule & Location
                </Typography>
              </Grid>
              {hasPastDates && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    ⚠️ <strong>Past Date Selected:</strong> You are creating a booking with a start date in the past.
                    This should only be done for historical records or special circumstances. Please confirm this is intentional.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.schedule.startDate}
                  onChange={(date) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, startDate: date }
                  }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={formData.schedule.endDate}
                  onChange={(date) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, endDate: date }
                  }))}
                  minDate={formData.schedule.startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={formData.schedule.startTime}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, startTime: e.target.value }
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={formData.schedule.endTime}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, endTime: e.target.value }
                  }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={popularLocations}
                  freeSolo
                  value={formData.schedule.pickupLocation}
                  onChange={(e, value) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, pickupLocation: value || '' }
                  }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pickup Location"
                      required
                      error={!!errors.pickupLocation}
                      helperText={errors.pickupLocation}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sameAsPickup}
                      onChange={(e) => {
                        setSameAsPickup(e.target.checked);
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            schedule: { ...prev.schedule, dropoffLocation: prev.schedule.pickupLocation }
                          }));
                        }
                      }}
                    />
                  }
                  label="Dropoff location same as pickup"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={popularLocations}
                  freeSolo
                  value={sameAsPickup ? formData.schedule.pickupLocation : (formData.schedule.dropoffLocation || '')}
                  onChange={(e, value) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, dropoffLocation: value || '' }
                  }))}
                  disabled={sameAsPickup}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dropoff Location (Optional)"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Pricing & Special Requests
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Daily Rate (₦)"
                type="number"
                required
                value={formData.pricing.dailyRate === 0 ? '' : formData.pricing.dailyRate}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  pricing: { ...prev.pricing, dailyRate: e.target.value === '' ? 0 : parseFloat(e.target.value) }
                }))}
                error={!!errors.dailyRate}
                helperText={errors.dailyRate || 'Enter the daily rental rate'}
                placeholder="e.g., 50000"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      ₦
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Days"
                type="number"
                value={formData.pricing.totalDays}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={specialRequestOptions}
                value={formData.specialRequests || []}
                onChange={(e, value) => setFormData(prev => ({
                  ...prev,
                  specialRequests: value
                }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Special Requests"
                    placeholder="Select additional services..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any special instructions or requirements..."
              />
            </Grid>
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Pricing Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal ({formData.pricing.totalDays} days):</Typography>
                    <Typography>₦{formData.pricing.subtotal?.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Taxes (7.5%):</Typography>
                    <Typography>₦{formData.pricing.taxes?.toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Service Fee:</Typography>
                    <Typography>₦{formData.pricing.fees?.toLocaleString()}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      ₦{formData.pricing.total?.toLocaleString()}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Review & Confirm Booking
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review all details before creating the booking.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Booking Details</Typography>
                  <Typography><strong>Type:</strong> {bookingTypeOptions.find(opt => opt.value === formData.type)?.label}</Typography>
                  <Typography><strong>Vehicle:</strong> {formData.vehicle.year ? `${formData.vehicle.year} ` : ''}{formData.vehicle.make} {formData.vehicle.model}</Typography>
                  {formData.driver?.name && (
                    <Typography><strong>Driver:</strong> {formData.driver.name}</Typography>
                  )}
                  <Typography><strong>Duration:</strong> {formData.pricing.totalDays} days</Typography>
                  <Typography><strong>Total:</strong> ₦{formData.pricing.total?.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Customer & Schedule</Typography>
                  <Typography><strong>Customer:</strong> {formData.customer.name}</Typography>
                  <Typography><strong>Email:</strong> {formData.customer.email}</Typography>
                  <Typography><strong>Phone:</strong> {formData.customer.phone}</Typography>
                  <Typography><strong>Pickup:</strong> {formData.schedule.pickupLocation}</Typography>
                  <Typography><strong>Dates:</strong> {formData.schedule.startDate?.toLocaleDateString()} - {formData.schedule.endDate?.toLocaleDateString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DirectionsCar />
          Create New Booking
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} orientation="horizontal" sx={{ mb: 4 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Booking'}
          </Button>
        )}
      </DialogActions>

      {/* Confirmation Dialog for Past Date Bookings */}
      <Dialog
        open={showPastDateConfirmation}
        onClose={() => setShowPastDateConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            ⚠️ Confirm Past Date Booking
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to create a booking with a start date in the past.
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>Start Date:</strong> {formData.schedule.startDate?.toLocaleDateString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Past date bookings should only be created for:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 2 }}>
            <li>Historical record-keeping</li>
            <li>Data migration purposes</li>
            <li>Backdating approved bookings</li>
          </Box>
          <Typography variant="body2" color="error">
            Are you sure you want to proceed with this past date booking?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPastDateConfirmation(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={performSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Yes, Create Past Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};